import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts'
import { EmptyState } from './BarListWidget'

export interface TrendSeries {
  key: string
  label: string
  color: string
}

/** Thin Recharts wrapper for the dashboard's line/area trend charts
 * (monthly attendance, check-in trends), with a consistent brand palette
 * and the same "No Record Found!" empty state used by the other widgets. */
export default function TrendChart({
  data,
  series,
  type = 'line',
  height = 220,
  yFormatter,
}: {
  data: Record<string, string | number>[]
  series: TrendSeries[]
  type?: 'line' | 'area'
  height?: number
  yFormatter?: (v: number) => string
}) {
  if (!data || data.length === 0) {
    return <EmptyState />
  }

  const Chart = type === 'area' ? AreaChart : LineChart

  return (
    <ResponsiveContainer width="100%" height={height}>
      <Chart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
        <defs>
          {series.map((s) => (
            <linearGradient key={s.key} id={`grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={s.color} stopOpacity={0.35} />
              <stop offset="95%" stopColor={s.color} stopOpacity={0.02} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
        <YAxis
          tick={{ fontSize: 11, fill: '#9ca3af' }}
          axisLine={false}
          tickLine={false}
          tickFormatter={yFormatter}
        />
        <Tooltip
          contentStyle={{ borderRadius: 8, border: '1px solid #f0f0f0', fontSize: 12 }}
          formatter={(v: number) => (yFormatter ? yFormatter(v) : v)}
        />
        {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {series.map((s) =>
          type === 'area' ? (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={s.color}
              fill={`url(#grad-${s.key})`}
              strokeWidth={2}
            />
          ) : (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={s.color}
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          )
        )}
      </Chart>
    </ResponsiveContainer>
  )
}
